/**
 * ModuleSwitcher — the topbar's "Sales ▾" mini-launcher (ADR-0038).
 *
 * With the sidebar rail retired, sideways navigation lives here: a dropdown
 * listing the same permission-gated modules as `/launch` (both derive from
 * `launcherTiles()`, so entitlements and ADR-0030 gating are inherited).
 * The trigger shows the current surface's name; the panel is a two-column
 * grid with the active module highlighted, and the footer carries the old
 * sidebar orphans (Settings · Help · Support) plus "All modules" → /launch.
 *
 * @see prototype/explorations/module-launcher.html — Frame 2 of the chrome
 *      exploration (docs/scratch history) mirrors this panel
 */
import { useMemo } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronDown, HelpCircle, LayoutGrid, LifeBuoy, Settings } from "lucide-react";
import {
  cn,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@flint/ui";
import { HELP_URL, SUPPORT_URL } from "../lib/nav-config.ts";
import { launcherTiles, type LauncherTile } from "../launcher/launcher-config.ts";
import { TINT_CLASS } from "../launcher/tints.ts";
import { TeammateAvatar } from "./teammate-avatar.tsx";

export interface ModuleSwitcherProps {
  modules: readonly string[];
  permissions: readonly string[];
}

/** The tile that owns a pathname (Home's "/" only matches exactly). */
function activeTileFor(tiles: LauncherTile[], pathname: string): LauncherTile | null {
  return (
    tiles.find((tile) =>
      tile.routes.some((route) =>
        route === "/" ? pathname === "/" : pathname === route || pathname.startsWith(`${route}/`),
      ),
    ) ?? null
  );
}

export function ModuleSwitcher({ modules, permissions }: ModuleSwitcherProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const tiles = useMemo(() => launcherTiles(modules, permissions), [modules, permissions]);
  // Settings isn't a tile's prefix match only — /settings/* pages should read
  // "Settings" in the trigger, which activeTileFor already covers via the
  // Settings tile. Anything unmatched (deep utility routes) reads "Modules".
  const active = activeTileFor(tiles, pathname);
  const label = active?.label ?? "Modules";
  const isOwner = permissions.includes("*");
  const canSettings = isOwner || permissions.includes("settings.manage");

  return (
    <DropdownMenuRoot>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Switch module — ${label}`}
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5",
            "text-sm font-semibold text-ink transition-colors hover:bg-raised",
            "data-[state=open]:bg-raised",
          )}
        >
          {label}
          <ChevronDown aria-hidden className="size-3.5 text-muted transition-transform" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={6} className="w-[480px] max-w-[calc(100vw-24px)] p-2.5">
        <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-2">
          {tiles.map((tile) => {
            const Icon = tile.icon;
            const isActive = tile.key === active?.key;
            return (
              <DropdownMenuItem key={tile.key} asChild className="p-0">
                <Link
                  to={tile.to}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-[7px] px-2.5 py-2",
                    isActive && "bg-sky-50",
                  )}
                >
                  {tile.persona ? (
                    <TeammateAvatar size={32} />
                  ) : (
                    <span
                      className={cn(
                        "inline-flex size-[30px] shrink-0 items-center justify-center rounded-lg",
                        TINT_CLASS[tile.tint],
                      )}
                    >
                      <Icon aria-hidden className="size-4" />
                    </span>
                  )}
                  <span className="min-w-0">
                    <span className={cn("block text-sm font-semibold leading-tight", isActive && "text-sky-700")}>
                      {tile.label}
                    </span>
                    {tile.sub ? (
                      <span className="block truncate text-xs leading-tight text-muted">{tile.sub}</span>
                    ) : null}
                  </span>
                </Link>
              </DropdownMenuItem>
            );
          })}
        </div>
        <div className="mt-2 flex items-center gap-4 border-t border-line px-2.5 pb-0.5 pt-2.5 text-xs font-medium">
          {canSettings ? (
            <Link to="/settings" className="inline-flex items-center gap-1.5 text-muted hover:text-sky-700">
              <Settings aria-hidden className="size-3.5" />
              Settings
            </Link>
          ) : null}
          <a
            href={HELP_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 text-muted hover:text-sky-700"
          >
            <HelpCircle aria-hidden className="size-3.5" />
            Help
          </a>
          <a href={SUPPORT_URL} className="inline-flex items-center gap-1.5 text-muted hover:text-sky-700">
            <LifeBuoy aria-hidden className="size-3.5" />
            Support
          </a>
          <Link to="/launch" className="ml-auto inline-flex items-center gap-1.5 text-sky-700 hover:underline">
            <LayoutGrid aria-hidden className="size-3.5" />
            All modules
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
}
